import React, { FC, ReactNode } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { DeprecatedFAIcon } from '@components-library/Icons/DeprecatedFAIcon';
import { getRequestString } from '@components-shared/table/utils/getRequestString';
import { KeyMapKeys, keyMap } from '@locales/lokalise/keyMap';
import { fetchApi } from 'api/fetchApi';
import cn from 'classnames';
import { ConfirmDialog } from 'components/shared/modal';
import { ITableRecordData } from 'global.interfaces';
import i18next from 'i18next';
import { handleError } from 'utils/errors';
import {
  TableActionsProps,
  TableActionDetails,
  TableActionLink,
  TableActionsMap,
  TableActionDefault,
  TableAction,
} from '../types';
import { COMMENT_FIELDS } from './modalFields';

const getActionName = (name: undefined | string | ((data: ITableRecordData) => string), data: ITableRecordData) =>
  typeof name === 'function' ? name(data) : i18next.t((name || 'actionName') as KeyMapKeys);

const onActionFn = (params: Omit<TableActionDefault, 'type'> & { type: string }, postParams?: any) => {
  const { name, data, setUpdatingState, updateData, onClick, api, queryStringParam } = params;

  if (onClick) {
    return onClick(data, postParams);
  }

  if (!api) {
    return null;
  }

  setUpdatingState();

  const requestString = getRequestString(api, data, queryStringParam);

  return fetchApi
    .post(requestString, postParams)
    .then(() => updateData(false))
    .catch((error) => {
      const messageOfFailedAction = getActionName(name, data);
      const errorMessage =
        params.errorMessage || typeof messageOfFailedAction === 'string'
          ? i18next.t(keyMap.sharedErrorsFailedMessageForRecord, {
              actionName: i18next.t(messageOfFailedAction as KeyMapKeys),
            })
          : i18next.t(keyMap.sharedErrorsOperationFailed);

      handleError(errorMessage, error);
      setUpdatingState(false);
    });
};

export const onAction = (params: Omit<TableActionDefault, 'type'> & { type: string }) => {
  const { confirm, data, name, confirmMessage, comment, commentKey = 'internalComment' } = params;

  if (confirm) {
    const actionName = getActionName(name, data);
    const confirmText =
      confirmMessage || i18next.t(keyMap.sharedConfirmMessage, { actionName: i18next.t(actionName as KeyMapKeys) });

    // TODO: HOW THIS EVEN WORKS?
    return (ConfirmDialog as any).show(
      confirmText,
      (commentText: any) => onActionFn(params, { [commentKey]: commentText }),
      null,
      comment,
    );
  }

  return onActionFn(params);
};

const renderActionIcon = (params: TableActionDetails | TableActionLink | Omit<TableActionDefault, 'type'>) => {
  const { iconClass } = params;

  if (iconClass == null) {
    return;
  }

  return typeof iconClass === 'string' ? (
    <span className="action-item__icon">
      <DeprecatedFAIcon icon={iconClass} />
    </span>
  ) : (
    <span className="action-item__icon">{iconClass}</span>
  );
};

const renderDefaultAction = (params: Omit<TableActionDefault, 'type'> & { type: string }, isModal?: boolean) => {
  const { name, data, disabled, className, setCurrentRow } = params;
  const title = getActionName(name, data);
  const isDisabled = typeof disabled === 'function' ? disabled(data) : !!disabled;

  return (
    <button
      className={cn('link-btn', className)}
      type="button"
      disabled={isDisabled}
      onClick={() => (isModal ? setCurrentRow({ ...params }) : onAction(params))}>
      {renderActionIcon(params)}
      {typeof title === 'string' ? i18next.t(title as KeyMapKeys) : title}
    </button>
  );
};

const renderDetailsAction = (params: TableActionDetails) => {
  const { name, data, disabled, changeShowDetails, component } = params;

  if (!component) {
    return null;
  }
  const title = getActionName(name, data);
  const isDisabled = typeof disabled === 'function' ? disabled(data) : !!disabled;

  return (
    <button className="link-btn" type="button" disabled={isDisabled} onClick={changeShowDetails}>
      {renderActionIcon(params)}
      {title}
    </button>
  );
};

const renderLinkAction = (props: TableActionLink) => {
  const { name = keyMap.sharedActionsDetails, location, link, data, actionKey = 'id' } = props;

  let linkUrl = '';

  const id = data[actionKey] ? `/${data[actionKey]}` : '';

  if (link) {
    linkUrl = typeof link === 'function' ? `${link(data)}${id}` : `${link}${id}`;
  } else {
    linkUrl = `${location.pathname}${id}`;
  }

  return (
    <Link className="link-btn" to={linkUrl}>
      {renderActionIcon(props)}
      {i18next.t(name as KeyMapKeys)}
    </Link>
  );
};

const ACTION_TYPES: {
  [actionType in keyof TableActionsMap]: (params: TableActionsMap[actionType]) => ReactNode;
} = {
  default: renderDefaultAction as any,
  details: renderDetailsAction,
  link: withRouter<any, any>(renderLinkAction) as any,
  comment: (params) => renderDefaultAction({ ...params, fields: COMMENT_FIELDS }, true),
  form: (params) =>
    renderDefaultAction(
      {
        ...params,
        fields: params.fields || COMMENT_FIELDS,
      },
      true,
    ),
};

const getActionTypeRenderer = (actionType: keyof TableActionsMap) =>
  ACTION_TYPES[actionType] as (params: TableActionsMap[keyof TableActionsMap]) => ReactNode;

export const Actions: FC<TableActionsProps> = ({
  actionList = [],
  additionalActionsClass,
  data,
  updateData,
  setUpdatingState,
  setCurrentRow,
  changeShowDetails,
}) => (
  <ul className={`actions-list ${additionalActionsClass}`}>
    {actionList.map((action, i) => {
      const key = `action_${i}`;
      const { isHidden } = action;
      const isHideAction = typeof isHidden === 'function' ? isHidden(data as ITableRecordData) : isHidden;

      if (isHideAction) {
        return null;
      }

      if (action.type && Object.prototype.hasOwnProperty.call(ACTION_TYPES, action.type)) {
        const renderAction = getActionTypeRenderer(action.type);

        return (
          <li key={key} className="action">
            {action.separator && <span className="vertical-separator">|</span>}
            {renderAction({
              ...action,
              updateData,
              data,
              setUpdatingState,
              setCurrentRow,
              changeShowDetails,
            } as TableAction)}
          </li>
        );
      }

      return (
        <li key={key} className="action">
          {action.separator && <span className="vertical-separator">|</span>}
          {ACTION_TYPES.default({
            ...(action as TableActionDefault),
            updateData,
            data,
            setUpdatingState,
          })}
        </li>
      );
    })}
  </ul>
);
